import { useEffect, useState } from "react";

import { useActAsStore, isActAsActive } from "@/stores/act-as-store";
import { useActAs } from "@/hooks/useActAs";

/**
 * Thin amber banner that sits at the very top of the app shell while an
 * admin is impersonating a target user.
 *
 * Ticks every 30s to refresh the countdown; if `expiresAt` has passed by the
 * time we next render, we clear the store and return null — the ky
 * afterResponse hook also clears on server 403, but local expiry is the
 * user-visible fast path.
 */
export function ActingAsBanner() {
  const actingAsUserId = useActAsStore((s) => s.actingAsUserId);
  const actingAsEmail = useActAsStore((s) => s.actingAsEmail);
  const expiresAt = useActAsStore((s) => s.expiresAt);
  const clearActingAs = useActAsStore((s) => s.clearActingAs);
  const { stop } = useActAs();

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Detect local expiry and clear in an effect to avoid setState-during-render.
  useEffect(() => {
    if (actingAsUserId && expiresAt !== null && expiresAt <= now) {
      clearActingAs();
    }
  }, [actingAsUserId, expiresAt, now, clearActingAs]);

  const active = isActAsActive({ actingAsUserId, expiresAt });
  if (!active || !expiresAt) return null;

  const remainingMs = Math.max(0, expiresAt - now);
  const mins = Math.floor(remainingMs / 60_000);

  return (
    <div
      role="status"
      className="sticky top-0 z-50 w-full bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between text-sm shadow-md border-b-2 border-amber-700"
    >
      <span className="flex items-center gap-2">
        <span className="rounded bg-amber-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
          Impersonating
        </span>
        <span>
          Acting as <strong>{actingAsEmail}</strong>
          {" "}&mdash; expires in {mins}m
        </span>
      </span>
      <button
        type="button"
        onClick={() => void stop()}
        className="rounded bg-amber-700 hover:bg-amber-800 px-2.5 py-1 text-xs font-semibold transition-colors"
      >
        Stop impersonation
      </button>
    </div>
  );
}
